import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  CircularProgress,
  TextField,
  MenuItem,
  Grid,
  Box,
  CardMedia,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  collection,
  onSnapshot,
  query,
  where,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { auth, db, getToken, uploadImage } from "../firebase";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { isLoggedIn } from "../session";
import Atras from "./atras";
import BottomNav from "./BottomNav";
import LogoLoader from "./LogoLoader";
import { CONTINENTS, filterPlaces } from "../placeSearch";

const MyProperties = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContinent, setSelectedContinent] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleContinentChange = (e) => {
    setSelectedContinent(e.target.value);
  };

  const handleDelete = async (propertyId) => {
    try {
      const propertyRef = doc(db, "places", propertyId);
      await deleteDoc(propertyRef);
    } catch (error) {
      console.error("Error al eliminar la propiedad:", error);
    }
  };

  const handleEditClick = (property) => {
    setSelectedProperty(property);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedProperty(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedProperty) return;

    try {
      const propertyRef = doc(db, "places", selectedProperty.id);
      await updateDoc(propertyRef, {
        title: selectedProperty.title,
        description: selectedProperty.description,
        price: selectedProperty.price,
        city: selectedProperty.city,
        place: selectedProperty.place,
        images: selectedProperty.images,
      });
      handleCloseModal();
    } catch (error) {
      console.error("Error al actualizar la propiedad:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedProperty((prev) => ({ ...prev, [name]: value }));
  };

  const formatDate = (timeToDo) => {
    if (timeToDo instanceof Date) {
      return timeToDo.toLocaleDateString();
    } else if (timeToDo && timeToDo.toDate instanceof Function) {
      return timeToDo.toDate().toLocaleDateString();
    }
    return "";
  };

  useEffect(() => {
    const checkAuthAndFetchProperties = async () => {
      if (!isLoggedIn()) {
        // Redirect to login page
        return;
      }

      try {
        const token = await getToken();
        if (!token) {
          console.error("No se pudo obtener el token de autenticación.");
          return;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.error("No hay un usuario autenticado.");
          return;
        }

        setLoading(true);
        const { uid } = currentUser;
        const q = query(collection(db, "places"), where("uid", "==", uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const propertiesData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setProperties(propertiesData);
          setLoading(false);
        });
        return () => unsubscribe();
      } catch (error) {
        console.error("Error al obtener propiedades:", error);
        setLoading(false);
      }
    };
    checkAuthAndFetchProperties();
  }, []);

  const handleImageUpload = async (event) => {
    const files = event.target.files; // Get all selected files

    if (!files) {
      return;
    }

    setLoading(true);

    try {
      const imageUrls = []; // Array to store URLs of all uploaded images

      for (const file of files) {
        const imageUrl = await uploadImage(file);
        imageUrls.push(imageUrl);
      }

      // Update the selectedProperty state directly
      setSelectedProperty((prevFormData) => ({
        ...prevFormData,
        images: [...prevFormData.images, ...imageUrls], // Add all new URLs to the array
      }));
    } catch (error) {
      console.error("Error uploading images:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProperties = filterPlaces(properties, searchTerm, selectedContinent);

  const handleRemoveImage = (index) => {
    setSelectedProperty((prevFormData) => ({
      ...prevFormData,
      images: prevFormData.images.filter((_, i) => i !== index),
    }));
  };

  const styles = {
    appBar: {
      bgcolor: "rgba(12, 26, 20, 0.95)",
      color: "black",
    },
    toolbar: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
      height: "auto",
      justifyContent: "space-around",
      alignItems: "center",
    },
    searchContainer: {
      display: "flex",
      width: "100%",
      justifyContent: "center",
      marginTop: 2,
      color: "#fff",

      
    },
    searchField: {
      width: "70%",
      color: "white", // This sets the label color
    },
    continentField: {
      width: "28%",
      ml: 1,
      color: "white",
    },
    container: {
      mt: 25,
      mb: 20,
    },
    card: {
      border: "2px solid grey",
      borderRadius: "10px",
      padding: "20px",
    },
    contentContainer: {
      padding: "16px",
    },
    title: {
      fontSize: "24px",
      fontWeight: "bold",
      marginBottom: "8px",
    },
    description: {
      fontSize: "14px",
      marginBottom: "8px",
    },
    price: {
      fontSize: "18px",
      fontWeight: "bold",
      color: "#4CAF50",
      marginBottom: "8px",
    },
    place: {
      fontSize: "14px",
      color: "#34d399",
      marginBottom: "8px",
    },
    created: {
      fontSize: "12px",
      color: "#999",
    },
    thumbnailContainer: {
      display: "flex",
      overflowX: "auto",
      marginTop: "10px",
    },
    thumbnail: {
      width: "100px",
      height: "100px",
      objectFit: "cover",
      marginRight: "10px",
      borderRadius: "5px",
    },
    imageContainer: {
      display: "flex",
      flexWrap: "wrap",
      marginTop: "10px",
    },
    image: {
      width: "100px",
      height: "100px",
      objectFit: "cover",
      marginRight: "10px",
      marginBottom: "10px",
      borderRadius: "5px",
      position: "relative",
    },
    imagePreview: {
      width: "100px",
      height: "100px",
      objectFit: "cover",
      margin: "5px",
      border: "1px solid #ccc",
      borderRadius: "5px",
    },
    deleteButton: {
      position: "absolute",
      top: "5px",
      right: "5px",
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      color: "white",
      padding: "5px",
      borderRadius: "50%",
      cursor: "pointer",
    },
  };

  return (
    <Container>
      <AppBar position="fixed" sx={styles.appBar}>
        <Toolbar sx={styles.toolbar}>
          <Atras />
          <Box>
            <Typography variant="h6" sx={{fontWeight: 500}} color="white">
              Mis Propiedades
            </Typography>
          </Box>
          <Box>
            
          </Box>
        </Toolbar>
        <Toolbar sx={{ display: "block", marginBottom: "20px" }}>
          <Box sx={styles.searchContainer}>
            <TextField
              label="Buscar por nombre, dirección o ciudad..."
              value={searchTerm}
              onChange={handleSearchChange}
              variant="outlined"
              sx={styles.searchField}
            />
            <TextField
              select
              label="Continente"
              value={selectedContinent}
              onChange={handleContinentChange}
              variant="outlined"
              sx={styles.continentField}
            >
              <MenuItem value="">Todos</MenuItem>
              {CONTINENTS.map((continent) => (
                <MenuItem key={continent} value={continent}>
                  {continent}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Toolbar>
      </AppBar>

      <Container sx={styles.container}>
        {loading ? (
          <LogoLoader label="Cargando propiedades..." minHeight={300} />
        ) : (
          <Grid container spacing={2} sx={{ mb: 20 }}>
            {filteredProperties.map((property) => (
              <Grid item key={property.id} xs={12} sm={6} md={4} lg={4}>
                <Box sx={styles.card}>
                  <Typography sx={styles.title}>{property.title}</Typography>
                  <Box sx={styles.contentContainer}>
                    <Typography sx={styles.description}>
                      {property.description}
                    </Typography>
                    <Typography sx={styles.price}>
                      Precio por día: $ {property.price}
                    </Typography>
                    <Typography sx={styles.place}>
                      Ciudad: {property.city}
                    </Typography>
                    <Typography sx={styles.place}>
                      Lugar: {property.place}
                    </Typography>
                    <Typography sx={styles.created}>
                      Fecha de publicación: {formatDate(property.createdAt)}
                    </Typography>
                    <Box sx={styles.thumbnailContainer}>
                      {property.images ? (
                        Array.isArray(property.images) ? (
                          property.images.map((image, index) => (
                            <CardMedia
                              key={index}
                              component="img"
                              image={image}
                              alt={`Imagen de ${property.title}`}
                              sx={styles.thumbnail}
                            />
                          ))
                        ) : (
                          <CardMedia
                            key={0}
                            component="img"
                            image={property.images}
                            alt={`Imagen de ${property.title}`}
                            sx={styles.thumbnail}
                          />
                        )
                      ) : (
                        <Box
                          sx={{
                            width: "100px",
                            height: "100px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: "#f0f0f0",
                            color: "#9fd2b7",
                          }}
                        >
                          <Typography>No hay imágenes</Typography>
                        </Box>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 2 }}
                      onClick={() => handleEditClick(property)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{ mt: 2 }}
                      onClick={() => handleDelete(property.id)}
                    >
                      Eliminar
                    </Button>
                    {property.googlePlaceId && property.claimStatus === "pending" && (
                      <Button
                        variant="contained"
                        fullWidth
                        sx={{ mt: 2 }}
                        onClick={() => navigate(`/verificar/${property.id}`)}
                      >
                        Verificar alojamiento
                      </Button>
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>

      <Dialog open={openModal} onClose={handleCloseModal}>
        <DialogTitle>Editar Propiedad</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="title"
            label="Título"
            type="text"
            fullWidth
            variant="outlined"
            value={selectedProperty?.title || ""}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="description"
            label="Descripción"
            type="text"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={selectedProperty?.description || ""}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="price"
            label="Precio por día"
            type="number"
            fullWidth
            variant="outlined"
            value={selectedProperty?.price || ""}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="city"
            label="Ciudad"
            type="text"
            fullWidth
            variant="outlined"
            value={selectedProperty?.city || ""}
            onChange={handleInputChange}
          />
          <TextField
            margin="dense"
            name="place"
            label="Lugar"
            type="text"
            fullWidth
            variant="outlined"
            value={selectedProperty?.place || ""}
            onChange={handleInputChange}
          />
          <label htmlFor="upload-button" style={{ width: "100%" }}>
            <Button
              variant="contained"
              component="div" // Cambiado a "div" para evitar el enfoque del botón
              sx={styles.uploadButton}
              disabled={loading}
            >
              <AddPhotoAlternateIcon sx={{ color: "black" }} />
              Subir Imagen del producto
              {loading && (
                <CircularProgress
                  size={40}
                  thickness={4}
                  sx={styles.progress}
                />
              )}
            </Button>
          </label>
          <input
            type="file"
            accept="image/*"
            onClick={(event) => (event.target.value = null)}
            onChange={handleImageUpload}
            style={{ display: "none" }}
            id="upload-button"
            multiple // Add the multiple attribute
          />
          {selectedProperty && selectedProperty.images && (
            <Grid container sx={{ mt: 2 }}>
              {selectedProperty.images.map((imageUrl, index) => (
                <Grid item xs={12} sm={6} md={4} key={index} margin={1}>
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      border: "1.5px solid black",
                      borderRadius: "10px",
                    }}
                  >
                    <img
                      src={imageUrl}
                      alt="Imagen de la propiedad"
                      style={styles.imagePreview}
                    />
                    <Button
                      variant="contained"
                      sx={styles.removeButton}
                      onClick={() => handleRemoveImage(index)}
                    >
                      X
                    </Button>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button onClick={handleSaveEdit}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <BottomNav />
    </Container>
  );
};

export default MyProperties;
